import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type {
  ChaveDeCadastro,
  EntradaDoCadastro,
  FiltrosDoCadastro,
  GrupoDeCadastro,
  LinhaDoCadastro,
  ListaDoCadastro,
  ResumoDosCadastros,
} from './cadastros.contrato';

/**
 * Camada de dados dos cadastros de referência. Dado real, da API
 * (`bff/v1/admin/parametros`); a regra — nome repetido, versão de concorrência,
 * quem pode escrever — é conferida no servidor e no banco (migrações 0012 e
 * 0300). Os cadastros que ainda não têm rota (documentos e financeiro) seguem em
 * `dados.ts`.
 */
export const CAMINHO_DA_API = '/bff/v1/admin/parametros';

export const TAMANHO_DA_PAGINA = 20;

export const CHAVES = {
  tudo: ['admin', 'parametrizacao', 'cadastros'] as const,
  resumo: ['admin', 'parametrizacao', 'cadastros', 'resumo'] as const,
  lista: (chave: ChaveDeCadastro, filtros: FiltrosDoCadastro) =>
    ['admin', 'parametrizacao', 'cadastros', 'lista', chave, filtros] as const,
  listas: (chave: ChaveDeCadastro) =>
    ['admin', 'parametrizacao', 'cadastros', 'lista', chave] as const,
  detalhe: (chave: ChaveDeCadastro, slug: string) =>
    ['admin', 'parametrizacao', 'cadastros', 'detalhe', chave, slug] as const,
  marcas: ['admin', 'parametrizacao', 'cadastros', 'opcoes-de-marca'] as const,
  modelos: ['admin', 'parametrizacao', 'cadastros', 'opcoes-de-modelo'] as const,
};

const base = (grupo: GrupoDeCadastro, chave: ChaveDeCadastro) =>
  `${CAMINHO_DA_API}/${grupo}/${chave}`;

/** Monta a consulta da lista. Só o que a pessoa escolheu vai para o endereço. */
export function consultaDaLista(filtros: FiltrosDoCadastro): string {
  const p = new URLSearchParams();
  p.set('pagina', String(filtros.pagina));
  p.set('tamanho', String(filtros.tamanho));
  if (filtros.busca.trim()) p.set('busca', filtros.busca.trim());
  if (filtros.situacao !== 'todos') p.set('situacao', filtros.situacao);
  if (filtros.ordenarPor) {
    p.set('ordenarPor', filtros.ordenarPor);
    p.set('sentido', filtros.sentido);
  }
  if (filtros.marcaId) p.set('marcaId', filtros.marcaId);
  if (filtros.modeloId) p.set('modeloId', filtros.modeloId);
  return p.toString();
}

export function useListaDoCadastro(
  grupo: GrupoDeCadastro,
  chave: ChaveDeCadastro,
  filtros: FiltrosDoCadastro,
) {
  return useQuery({
    queryKey: CHAVES.lista(chave, filtros),
    queryFn: () => chamarApi<ListaDoCadastro>(`${base(grupo, chave)}?${consultaDaLista(filtros)}`),
    // Trocar de página não pisca a tabela: a anterior fica até a nova chegar.
    placeholderData: keepPreviousData,
    retry: naoInsistirEmRecusa,
  });
}

/** Detalhe pelo slug do endereço da tela. */
export function useDetalhePorSlug(
  grupo: GrupoDeCadastro,
  chave: ChaveDeCadastro,
  slug: string,
  habilitado = true,
) {
  return useQuery({
    queryKey: CHAVES.detalhe(chave, slug),
    enabled: habilitado,
    queryFn: () =>
      chamarApi<LinhaDoCadastro>(`${base(grupo, chave)}/por-slug/${encodeURIComponent(slug)}`),
    // Registro que não existe não passa a existir na segunda tentativa.
    retry: (tentativas, erro) =>
      !(erro instanceof ErroApi && erro.situacao === 404) && naoInsistirEmRecusa(tentativas, erro),
  });
}

/** Total e ativos de cada cadastro, para os cartões dos hubs. */
export function useResumoDosCadastros() {
  return useQuery({
    queryKey: CHAVES.resumo,
    queryFn: () => chamarApi<ResumoDosCadastros>(`${CAMINHO_DA_API}/cadastros/resumo`),
    retry: naoInsistirEmRecusa,
  });
}

/** Marcas ativas, para o seletor do formulário de modelo. */
export function useOpcoesDeMarca(habilitado: boolean) {
  return useQuery({
    queryKey: CHAVES.marcas,
    enabled: habilitado,
    queryFn: () =>
      chamarApi<ListaDoCadastro>(
        `${base('cadastros', 'marcas-veiculo')}?situacao=ativo&tamanho=100&ordenarPor=nome&sentido=asc`,
      ),
    retry: naoInsistirEmRecusa,
  });
}

/** Quantas páginas de 100 o seletor de modelos busca, no máximo. */
const PAGINAS_DE_MODELOS = 20;

/**
 * Modelos ativos, com a marca, para o seletor do formulário de versão. A API
 * pagina em até 100; o seletor junta as páginas (o catálogo cresce com a
 * fonte oficial).
 */
export function useOpcoesDeModelo(habilitado: boolean) {
  return useQuery({
    queryKey: CHAVES.modelos,
    enabled: habilitado,
    queryFn: async () => {
      const itens: LinhaDoCadastro[] = [];
      for (let pagina = 1; pagina <= PAGINAS_DE_MODELOS; pagina += 1) {
        const r = await chamarApi<ListaDoCadastro>(
          `${base('cadastros', 'modelos-veiculo')}?situacao=ativo&tamanho=100&pagina=${pagina}&ordenarPor=marcaNome&sentido=asc`,
        );
        itens.push(...r.itens);
        if (itens.length >= r.total || r.itens.length === 0) break;
      }
      return itens;
    },
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Toda escrita invalida as listas do cadastro, o detalhe, o resumo dos hubs e,
 * quando muda uma marca, o seletor de marcas.
 */
function useAposEscrever(chave: ChaveDeCadastro) {
  const cliente = useQueryClient();
  return () => {
    void cliente.invalidateQueries({ queryKey: CHAVES.listas(chave) });
    void cliente.invalidateQueries({ queryKey: [...CHAVES.tudo, 'detalhe', chave] });
    void cliente.invalidateQueries({ queryKey: CHAVES.resumo });
    if (chave === 'marcas-veiculo') void cliente.invalidateQueries({ queryKey: CHAVES.marcas });
    if (chave === 'marcas-veiculo' || chave === 'modelos-veiculo') {
      void cliente.invalidateQueries({ queryKey: CHAVES.modelos });
    }
    // Grafia e item novo mexem na fila de conferência da fonte oficial.
    if (chave === 'marcas-veiculo' || chave === 'modelos-veiculo' || chave === 'versoes-veiculo') {
      void cliente.invalidateQueries({ queryKey: ['admin', 'catalogo-veiculos'] });
    }
  };
}

export function useCriarCadastro(grupo: GrupoDeCadastro, chave: ChaveDeCadastro) {
  const aposEscrever = useAposEscrever(chave);
  return useMutation({
    mutationFn: (entrada: EntradaDoCadastro) =>
      chamarApi<LinhaDoCadastro>(base(grupo, chave), { metodo: 'POST', corpo: entrada }),
    onSuccess: aposEscrever,
  });
}

export function useAlterarCadastro(grupo: GrupoDeCadastro, chave: ChaveDeCadastro) {
  const aposEscrever = useAposEscrever(chave);
  return useMutation({
    mutationFn: ({ id, entrada }: { id: string; entrada: EntradaDoCadastro }) =>
      chamarApi<LinhaDoCadastro>(`${base(grupo, chave)}/${id}`, { metodo: 'PUT', corpo: entrada }),
    onSuccess: aposEscrever,
  });
}

export function useMudarSituacao(grupo: GrupoDeCadastro, chave: ChaveDeCadastro) {
  const aposEscrever = useAposEscrever(chave);
  return useMutation({
    mutationFn: ({ id, ativar, versao }: { id: string; ativar: boolean; versao: string }) =>
      chamarApi<LinhaDoCadastro>(
        `${base(grupo, chave)}/${id}/${ativar ? 'reativar' : 'inativar'}`,
        { metodo: 'POST', corpo: { versao } },
      ),
    onSuccess: aposEscrever,
  });
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
